import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomSelect, Creatable } from "../Shared/Select/Select";
import { useSelector, useDispatch } from "react-redux";
import isURL from "validator/lib/isURL";
import { getTypes } from "../../actions/sql/typeGroups";
import { fetchDcmLandingPages } from "../../actions/dcm/landingPages";
import { typeGroupNames, getSelectValue } from "../../utils/dataUtils";
import { landingPageScopeKey } from "../../utils/landingPages";
import { addCustomLandingPage } from "../../actions/dcm/landingPages";
import {
  useUserAdvertisers,
  ADVERTISER_STATUS
} from "../../hooks/useUserAdvertisers";

import {
  Grid,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Link,
  makeStyles
} from "@material-ui/core";

import MuiAlert from "@material-ui/lab/Alert";

import Label from "../Shared/Label";
import HelperTooltip from "../Shared/HelperTooltip/HelperTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%"
  },
  paper: {
    padding: "20px",
    height: "100%"
  },
  gridSection: {
    margin: "0px 0 10px 0",
    "&:last-child": {
      margin: "20px 0 20px 0"
    }
  },
  textInput: {
    margin: 0,
    "& .MuiOutlinedInput-root.Mui-disabled": {
      "& fieldset": {
        borderColor: "rgba(230, 230, 230, 0.9)"
      }
    }
  },
  textInputDisabled: {
    backgroundColor: "rgba(242, 242, 242, 1)"
  },
  helperText: {
    margin: 0
  },
  buttonStyle: {
    marginRight: 12
  },
  typography: {
    marginLeft: 12,
    paddingBottom: 24,
    textAlign: "left",
    wordBreak: "break-all"
  },
  buttons: {
    marginLeft: "16px !important"
  },
  // Status rows rendered inside a react-select menu, in place of options.
  selectStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "left",
    // Not `gap`: JSS's default-unit map covers column-gap and grid-gap but not
    // plain gap, so theme.spacing(1) emits a unitless `gap: 8` that the browser
    // drops. Every child is an element so this margin rule can space them.
    "& > * + *": {
      marginLeft: theme.spacing(1)
    }
  },
  selectStatusError: {
    color: theme.palette.error.main
  }
}));

const CreativeNameGenerator = ({
  taxonomy,
  handleSelect,
  handleClearValues,
  handleSave,
  handleTextInput,
  isDisabled,
  errors,
  standalone,
  isDcmCreative,
  setAlert,
  fileDimensions,
  selectedCreative,
  isRetina,
  onRetinaToggle,
  isVideoCreative,
  advertiserId
}) => {
  let formGridSpacing = 3;
  let classes = useStyles();
  let dispatch = useDispatch();
  let typeGroupData = useSelector((state) => state.typeGroups);
  let landingPageData = useSelector((state) => state.dcmLandingPages);
  let [selectedTypeIds, setSelectedTypeIds] = useState({});
  const campaigns = useSelector((state) => state.dcmCampaigns);

  useEffect(() => {
    if (typeGroupData.allGroupNames.length === 0)
      dispatch(getTypes(typeGroupNames));
  }, [dispatch, typeGroupData.allGroupNames.length]);

  const selectedCampaign = campaigns.byId[campaigns.selectedId];
  const campaignAdvertiserId = selectedCampaign
    ? selectedCampaign.advertiserId
    : null;

  // Only needed when neither the caller nor the selected campaign already
  // narrows the URL list to one advertiser.
  const {
    advertisers: userAdvertisers,
    status: advertiserStatus,
    message: advertiserMessage,
    retry: retryUserAdvertisers
  } = useUserAdvertisers({
    enabled: !advertiserId && !campaignAdvertiserId
  });

  // Caller scope (Preloader advertiser switcher) wins, then the selected
  // campaign's advertiser, then every advertiser the user can see.
  const urlAdvertiserIds = useMemo(() => {
    if (advertiserId) return [advertiserId];
    if (campaignAdvertiserId) return [campaignAdvertiserId];
    // userAdvertisers is [{id, name}] — extract IDs for the API
    return userAdvertisers.map((a) => (a && a.id ? a.id : a));
  }, [advertiserId, campaignAdvertiserId, userAdvertisers]);

  const urlScopeKey = useMemo(
    () => landingPageScopeKey(urlAdvertiserIds),
    [urlAdvertiserIds]
  );

  const loadLandingPages = useCallback(() => {
    dispatch(fetchDcmLandingPages(urlAdvertiserIds));
  }, [dispatch, urlAdvertiserIds]);

  useEffect(() => {
    if (standalone) return;
    // Nothing to scope the request by yet. This used to read
    // `userAdvertisers != ""`, which is false for [] — so a failed advertiser
    // fetch (or a campaign-scoped session, where the list is never fetched at
    // all) silently parked the URL fetch and the dropdown just sat empty.
    if (!urlScopeKey) return;
    if (landingPageData.isLoading) return;
    // Gate on the scope we already requested, not on whether the list came back
    // empty. An advertiser with no non-archived landing pages succeeds with
    // `[]`; because the reducer returns a fresh object each time, an
    // empty-list guard re-ran this effect on every store update and hammered
    // the endpoint forever. This also covers the failure case (loadedKey
    // survives a FAILURE, so retry stays user-driven via the Retry link) and
    // picks up an advertiser switch for free — a new scope is a new key.
    if (landingPageData.loadedKey === urlScopeKey) return;

    loadLandingPages();
  }, [landingPageData, standalone, urlScopeKey, loadLandingPages]);

  const setVisibleTypes = (option, action) => {
    let typeIds = { ...selectedTypeIds };
    if (option.id) {
      typeIds[action.name] = option.id;
    }
    setSelectedTypeIds(typeIds);
    handleSelect(option, action);
  };

  const showVisibleTypes = (typeData) => {
    if (typeData.length > 0) {
      if (typeData[0].parentTypeMatches.length > 0) {
        let filteredData = typeData.filter((type) => {
          let selectedIds = Object.values(selectedTypeIds);
          type.parentTypeMatches.map((parentId) =>
            selectedIds.includes(parentId)
          );
          return false;
        });

        if (filteredData.length === 0) return typeData;
      } else {
        return typeData;
      }
    }
  };

  const modelYearData = () => {
    let currentYear = new Date().getFullYear();
    return [
      { value: currentYear - 1, label: currentYear - 1 },
      { value: currentYear, label: currentYear },
      { value: currentYear + 1, label: currentYear + 1 },
      { value: currentYear + 2, label: currentYear + 2 }
    ];
  };

  const getURLData = (id) => {
    if (
      isDcmCreative &&
      selectedCreative.hasOwnProperty("clickTags") &&
      !selectedCreative.taxonomy.urlId
    ) {
      id = selectedCreative.clickTags[0].clickThroughUrl.landingPageId;
      return landingPageData.byId[id];
    } else if (id) {
      return landingPageData.byId[id];
    } else return "";
  };

  const nameGeneratorHeading = () => {
    let RMTypes = [
      "RICH_MEDIA_DISPLAY_BANNER",
      "RICH_MEDIA_DISPLAY_EXPANDING",
      "RICH_MEDIA_DISPLAY_INTERSTITIAL"
    ];

    let heading = "instructions here!!";
    if (isDcmCreative && isDcmCreative.hasOwnProperty("clickTags")) {
      heading =
        "Note: A URL does not need to be selected for existing H5 creatives. If there is a URL change, please select a URL from the dropdown or add a new URL.";
    } else if (isDcmCreative && RMTypes.includes(isDcmCreative.type)) {
      heading = "Note: A URL does not need to be selected for existing Rich Media creatives. If there is a URL change, please select a URL from the dropdown or add a new URL.";
    } else if (selectedCreative && selectedCreative.creativeRole === "COMPANION" && selectedCreative.file && selectedCreative.file.id) {
      heading = "This companion is an existing Campaign Manager creative and does not need naming.";
    } else if (isDcmCreative) {
      heading = "This is an existing Campaign Manager creative. Only the URL can be updated.";
    }
    else if (taxonomy.name) {
      heading = "";
    } else if (standalone) {
      heading = "Add and select a new name on left before generating name";
    } else {
      heading = "Select a creative from left before generating name";
    }
    return heading;
  };

  // react-select renders these in place of the option list, so a user who opens
  // the URL dropdown mid-fetch sees why it's empty instead of assuming there are
  // no URLs. Both only render while the menu is open.
  const renderUrlLoadingMessage = () => (
    <span className={classes.selectStatus}>
      <CircularProgress size={16} thickness={5} color="inherit" />
      <span>Loading URLs…</span>
    </span>
  );

  const renderUrlNoOptionsMessage = () => {
    // URLs can't load until we know which advertisers to ask for, so a failed
    // advertiser lookup has to explain itself here too — otherwise the dropdown
    // claims there are simply no URLs.
    let failure = null;
    if (landingPageData.error) {
      failure = { text: "Couldn't load URLs.", retry: loadLandingPages };
    } else if (advertiserStatus === ADVERTISER_STATUS.ERROR) {
      failure = { text: advertiserMessage, retry: retryUserAdvertisers };
    }
    if (!failure) return "No URLs found";
    return (
      <span className={`${classes.selectStatus} ${classes.selectStatusError}`}>
        <span>{failure.text}</span>
        <Link
          component="button"
          type="button"
          variant="body2"
          color="inherit"
          underline="always"
          // mousedown, not click: react-select's own menu mousedown handler
          // preventDefaults to keep focus on its input, so this has to fire in
          // the same phase to be sure it lands. preventDefault here keeps the
          // menu open, so the retry's spinner is visible in place.
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            failure.retry();
          }}
        >
          Retry
        </Link>
      </span>
    );
  };

  const handleCreateUrl = (inputValue) => {
    let action = { name: "urlId" };
    //add URL validation here

    if (isURL(inputValue)) {
      let { landingPage } = dispatch(addCustomLandingPage(inputValue));
      handleSelect(landingPage, action);
    } else {
      //throw error alert
      setAlert("Not a valid URL. Please be sure to include http(s).", "error");
    }
  };

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item>
          <Typography variant="body1" className={classes.typography}>
            {nameGeneratorHeading()}
          </Typography>
        </Grid>
        {selectedCreative && selectedCreative.file && selectedCreative.file.type &&
          (selectedCreative.file.type.startsWith("audio/") || selectedCreative.file.type.startsWith("video/")) &&
          selectedCreative.companionId && (
            <Grid item xs={12} style={{ marginLeft: 12, marginRight: 12, paddingBottom: 12 }}>
              <MuiAlert severity="info" variant="outlined">
                Audio/video creatives and their companions must be named separately.
                Select the companion creative from the list to name it.
              </MuiAlert>
            </Grid>
          )}
        <Grid
          classes={{ container: classes.gridSection }}
          item
          container
          spacing={formGridSpacing}
        >
          {standalone ? null : (
            <Grid item lg={12} xs={12} style={{ paddingTop: 0 }}>
              <Label>
                URL (the URL will be applied to all the creatives highlighted in
                blue)
              </Label>
              <CustomSelect
                name="urlId"
                isDisabled={typeGroupData.isLoading || isDisabled}
                isLoading={
                  landingPageData.isLoading ||
                  advertiserStatus === ADVERTISER_STATUS.LOADING
                }
                loadingMessage={renderUrlLoadingMessage}
                noOptionsMessage={renderUrlNoOptionsMessage}
                value={getURLData(taxonomy.urlId)}
                getOptionLabel={(option) => option.url}
                getOptionValue={(option) => option.id}
                // getNewOptionData={(inputValue, optionLabel) => ({
                //   url: optionLabel,
                //   id: inputValue,
                //   __isNew__: true
                // })}
                onChange={setVisibleTypes}
                // onCreateOption={handleCreateUrl}
                options={Object.values(landingPageData.byId)}
                error={errors.urlId.isError}
              />
            </Grid>
          )}

          {!isDcmCreative && (
            <>
              <Grid item lg={6} xs={12}>
                <Label>Make</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="make"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Make"].types,
                    taxonomy.make
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Make"].types}
                  error={errors.make.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Model Year</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="modelYear"
                  value={getSelectValue(modelYearData(), taxonomy.modelYear)}
                  onChange={setVisibleTypes}
                  options={modelYearData()}
                  error={errors.modelYear.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Model</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="model"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Model"].types,
                    taxonomy.model
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={showVisibleTypes(
                    typeGroupData.byGroupName["Model"].types
                  )}
                  error={errors.model.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>
                  Trim
                  <HelperTooltip tooltipText="If field does not apply, please leave blank." />
                </Label>
                <TextField
                  classes={{ root: classes.textInput }}
                  variant="outlined"
                  margin="dense"
                  hiddenLabel
                  name="trim"
                  onChange={handleTextInput}
                  value={taxonomy.trim || ""}
                  disabled={typeGroupData.isLoading || isDisabled}
                  error={errors.trim.isError}
                  helperText={errors.trim.message}
                  fullWidth
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Creative Agency</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="creativeAgency"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Creative Agency"].types,
                    taxonomy.creativeAgency
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Creative Agency"].types}
                  error={errors.creativeAgency.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Strategy</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="strategy"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Strategy"].types,
                    taxonomy.strategy
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Strategy"].types}
                  error={errors.strategy.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Retail Offer Type</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="retailOfferType"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Retail Offer Type"].types,
                    taxonomy.retailOfferType
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Retail Offer Type"].types}
                  error={errors.retailOfferType.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Creative Unit</Label>
                <TextField
                  classes={{ root: classes.textInput }}
                  variant="outlined"
                  margin="dense"
                  hiddenLabel
                  name="creativeUnit"
                  onChange={handleTextInput}
                  value={taxonomy.creativeUnit || ""}
                  disabled={typeGroupData.isLoading || isDisabled}
                  error={errors.creativeUnit.isError}
                  helperText={errors.creativeUnit.message}
                  fullWidth
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>
                  Event/Offer/Promotion/Ad ID
                  <HelperTooltip tooltipText="Please indicate the expiration of retail offers (non-feed dynamic creative) in the Event/Offer/Promotion/Ad ID field. If field does not apply, please leave blank. For more information, please visit the Help page." />
                </Label>
                <TextField
                  classes={{ root: classes.textInput }}
                  variant="outlined"
                  margin="dense"
                  hiddenLabel
                  name="extraInfo"
                  onChange={handleTextInput}
                  value={taxonomy.extraInfo || ""}
                  disabled={typeGroupData.isLoading || isDisabled}
                  error={errors.extraInfo.isError}
                  helperText={errors.extraInfo.message}
                  fullWidth
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Format</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="format"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Format"].types,
                    taxonomy.format
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Format"].types}
                  error={errors.format.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Country</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="country"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Country"].types,
                    taxonomy.country
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Country"].types}
                  error={errors.country.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Language</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="language"
                  value={getSelectValue(
                    typeGroupData.byGroupName["Language"].types,
                    taxonomy.language
                  )}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Language"].types}
                  error={errors.language.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Creative Version</Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled}
                  name="creativeVersion"
                  onChange={setVisibleTypes}
                  value={getSelectValue(
                    typeGroupData.byGroupName["Creative Version"].types,
                    taxonomy.creativeVersion
                  )}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Creative Version"].types}
                  error={errors.creativeVersion.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Dimensions</Label>
                <CustomSelect
                  isDisabled={
                    typeGroupData.isLoading || isDisabled || fileDimensions || isRetina
                  }
                  name="dimensions"
                  value={(() => {
                    let dimValue = !taxonomy.dimensions && fileDimensions
                      ? fileDimensions
                      : taxonomy.dimensions;
                    let found = getSelectValue(
                      typeGroupData.byGroupName["Dimensions"].types,
                      dimValue
                    );
                    // If retina halved to a value not in the list, show it as a custom entry
                    if (!found && dimValue && isRetina) {
                      return { name: dimValue, value: dimValue };
                    }
                    return found;
                  })()}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Dimensions"].types}
                  error={errors.dimensions.isError}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Label>Creative Display
                  <HelperTooltip tooltipText="GM global standard of a video View. A user views the video and is exposed to the brand, nameplate and/or tag line. The view will not count if the user sees less than 25% of its entirety (1st quartile), or the end of quartile where the brand integration exists. Video creative names need to include the Quartile where the branded view of the creative running will occur. Any non-video creative needs NA listed." />
                </Label>
                <CustomSelect
                  isDisabled={typeGroupData.isLoading || isDisabled || !isVideoCreative}
                  name="creativeDisplay"
                  value={(() => {
                    let found = getSelectValue(
                      typeGroupData.byGroupName["Creative Display"].types,
                      taxonomy.creativeDisplay
                    );
                    // For non-video creatives auto-set to N/A, show as custom entry if not in list
                    if (!found && taxonomy.creativeDisplay && !isVideoCreative) {
                      return { name: taxonomy.creativeDisplay, value: taxonomy.creativeDisplay };
                    }
                    return found;
                  })()}
                  onChange={setVisibleTypes}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  options={typeGroupData.byGroupName["Creative Display"].types.filter(
                    (type) => !isVideoCreative || type.value !== "NA"
                  )}
                  error={errors.creativeDisplay.isError}
                />
              </Grid>
              {selectedCreative && selectedCreative.file && selectedCreative.file.type &&
                selectedCreative.file.type.startsWith("image/") &&
                selectedCreative.creativeRole === "PRIMARY" && (
                <Grid item lg={12} xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRetina || false}
                        onChange={(e) => onRetinaToggle(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Retina (2x) — image is double the placement size"
                  />
                </Grid>
              )}
              <Grid item lg={12} xs={12}>
                <Label>Notes (optional)</Label>
                <TextField
                  classes={{ root: classes.textInput }}
                  variant="outlined"
                  margin="dense"
                  hiddenLabel
                  name="notes"
                  onChange={handleTextInput}
                  value={taxonomy.notes || ""}
                  disabled={typeGroupData.isLoading || isDisabled}
                  fullWidth
                  multiline
                  rows="4"
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid
          classes={{ container: classes.gridSection }}
          item
          container
          justify="flex-start"
          className={classes.buttons}
        >
          <Grid item>
            <Button
              disabled={isDisabled}
              variant="contained"
              onClick={handleSave}
              className={classes.buttonStyle}
              color="secondary"
              disableElevation
            >
              {isDcmCreative ? "Save URL" : "Save Name"}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleClearValues}
              className={classes.buttonStyle}
              disableElevation
            >
              Clear Values
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

CreativeNameGenerator.defaultProps = {
  creative: {
    taxonomy: {
      name: "default»creative»name»taxonomy»and»things"
    }
  }
};

export default CreativeNameGenerator;
